import { useStore } from '@nanostores/preact'
import { useRef, useState, useMemo } from 'preact/hooks'
import { FormState, FORM_ERROR } from 'final-form'
import { Field, Form, FormRenderProps, FormSpy } from 'react-final-form'
import classNames from 'utils/preact/class-names'

import { checkEmail, checkPassword } from './services/validation'
import { useTranslation } from './translation'
import { profile, SubscriptionValue } from './store'
import { FocusFirstError } from './focus-first-error'
import { Currency, IsoCountry, Products } from './support'
import css from './style.module.scss'
import headerLogo from './static/surf-forecast_outside--header_logo.webp'
import surfWhite from './static/surf-forecast__logo--white.svg'
import outsideLogo from './static/outside-logo--white.svg'

interface FormValues {
  email: string
  password: string
  subscription: SubscriptionValue
}

export function AuthenticationSubscribe({
  token,
  onAuthenticate,
  onPasswordForgot,
  onAuthenticatedContribute,
  onSignUp
}: {
  token: string
  products: Products
  currencies: Currency[]
  isoCountries: IsoCountry[]
  onAuthenticate: () => void
  onSignUp: () => void
  onPasswordForgot: () => void
  onAuthenticatedContribute: (subscription: SubscriptionValue) => void
  onUnathenticatedContribute: (subscription: SubscriptionValue) => void
}) {
  const t = useTranslation()
  const user = useStore(profile)
  const [isContributed, setContribute] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  const formRef = useRef<HTMLFormElement>(null)
  const decorator = useMemo(() => FocusFirstError(formRef), [])

  return (
    <div className={css.content}>
      <a href="/">
        <img src={headerLogo} alt="Surf Forecast" height={32} />
      </a>
      <Form
        initialValues={{ subscription: user.subscription }}
        decorators={[decorator]}
        onSubmit={(values: FormValues): Promise<Object | undefined> =>
          fetch('/session', {
            method: 'post',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-CSRF-Token': token
            },
            credentials: 'include',
            body: JSON.stringify(values)
          })
            .then((res) => {
              if (!res.ok) {
                if (res.status !== 422 && res.status !== 401) {
                  return {
                    [FORM_ERROR]: `${res.status}: ${res.statusText}`
                  }
                }
              }

              return res.json()
            })
            .then((res) => {
              if (res.user) {
                if (res.user.paymentProfile) {
                  profile.setKey('paymentProfile', res.user.paymentProfile)
                }
                profile.setKey('emailVerified', res.user.emailVerified)
                profile.setKey('premium', res.user.premium)
              }

              if (res.success) return undefined
              if (res.errors) return res.errors
              return res
            })
            .catch((err: Error) => ({
              [FORM_ERROR]: err.message
            }))
        }
      >
        {({
          hasValidationErrors,
          hasSubmitErrors,
          submitErrors,
          submitError,
          submitting,
          pristine,
          form,
          handleSubmit
        }: FormRenderProps & FormState<FormValues>) => (
          <form
            action="/session"
            method="post"
            onSubmit={(event: Event) => {
              // assume the function was called by pressing enter, mobile next button, or 'log in' button
              setContribute(false)
              handleSubmit(event)
            }}
            ref={formRef}
          >
            <FormSpy
              subscription={{ pristine: true }}
              onChange={() => {
                if (!isInitialized) {
                  form.batch(() => {
                    form.change('email', user.email)
                    form.change('subscription', user.subscription)
                  })
                  setIsInitialized(true)
                }
              }}
            />
            <FormSpy
              subscription={{ submitSucceeded: true, values: true }}
              onChange={({
                submitSucceeded,
                values
              }: {
                submitSucceeded: boolean
                values: FormValues
              }) => {
                if (!submitSucceeded) return

                profile.setKey('email', values.email)
                profile.setKey('subscription', values.subscription)
                profile.setKey('reloadRequired', true)

                if (isContributed) onAuthenticatedContribute(values.subscription)
                else onAuthenticate()
              }}
            />
            <div className={css['form-container']}>
              <p className={css.caption}>
                Please sign in with your Surf-Forecast account details below.
              </p>
              <Field
                name="email"
                validate={(value) => checkEmail(t, value)}
                render={({ input, meta }) => (
                  <label
                    className={classNames({
                      [css.label]: true,
                      [css['is-valid']]: meta.touched && meta.valid,
                      [css['is-invalid']]: meta.touched && meta.invalid
                    })}
                    htmlFor="email"
                  >
                    <div className={css['form-label']}>
                      <span className={css.name}>Email or username</span>
                      <div className={css.error}>
                        {(() => {
                          if (meta.touched && meta.error) return `(${meta.error})`
                          if (meta.submitError) return `(${meta.submitError})`
                          return null
                        })()}
                      </div>
                    </div>
                    <div
                      className={classNames({
                        [css['input-wrapper']]: true,
                        [css['is-valid']]: meta.touched && meta.valid,
                        [css['is-invalid']]: meta.touched && meta.invalid
                      })}
                    >
                      <input
                        {...input}
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="Email or username"
                        className={classNames({
                          [css.input]: true,
                          [css['is-valid']]: meta.touched && meta.valid,
                          [css['is-invalid']]: meta.touched && meta.invalid
                        })}
                      />
                    </div>
                  </label>
                )}
              />
              <Field
                name="password"
                validate={(value) => checkPassword(t, value)}
                render={({ input, meta }) => (
                  <label
                    className={classNames({
                      [css.label]: true,
                      [css['is-valid']]: meta.touched && meta.valid,
                      [css['is-invalid']]: meta.touched && meta.invalid
                    })}
                    htmlFor="password"
                  >
                    <div className={css['form-label']}>
                      <span className={css.name}>{t('password')}</span>
                      <div className={css.error}>
                        {(() => {
                          if (meta.touched && meta.error) return `(${meta.error})`
                          if (meta.submitError) return `(${meta.submitError})`
                          return null
                        })()}
                      </div>
                    </div>
                    <div
                      className={classNames({
                        [css['input-wrapper']]: true,
                        [css['is-valid']]: meta.touched && meta.valid,
                        [css['is-invalid']]: meta.touched && meta.invalid
                      })}
                    >
                      <input
                        {...input}
                        id="password"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Password"
                        className={classNames({
                          [css.input]: true,
                          [css['is-valid']]: meta.touched && meta.valid,
                          [css['is-invalid']]: meta.touched && meta.invalid
                        })}
                      />
                    </div>
                    <a
                      className={classNames({
                        [css['text-highlight']]: true,
                        [css['font-regular']]: true,
                        [css['text-underline']]: true
                      })}
                      href="/passwords/new"
                      onClick={(e) => {
                        e.preventDefault()
                        onPasswordForgot()
                      }}
                    >
                      {t('forgot_password')}
                    </a>
                  </label>
                )}
              />

              {hasSubmitErrors && (
                <p
                  className={classNames({
                    [css['text-medium']]: true,
                    [css['text-center']]: true,
                    [css['error-highlight']]: true
                  })}
                >
                  {submitError || submitErrors}
                </p>
              )}

              <button
                className={classNames({
                  [css.submit]: true,
                  [css['is-loading']]: submitting
                })}
                type="submit"
                id="sign-in"
                disabled={pristine || hasValidationErrors}
              >
                Sign in with
                <img src={surfWhite} alt="Surf Forecast" width={100} />
              </button>

              <p className={css.caption}>
                Alternatively, you can now use your Outside account to sign in to any brand in the
                Outside digital ecosystem, including <a href="/">Surf-Forecast</a>,{' '}
                <a href="https://www.snow-forecast.com/" target="_blank" rel="noreferrer">
                  Snow-Forecast
                </a>
                , and{' '}
                <a href="https://www.mountain-forecast.com/" target="_blank" rel="noreferrer">
                  Mountain-Forecast
                </a>
                .
              </p>

              <a href="/" className={css.outside}>
                Sign in with
                <img src={outsideLogo} alt="Outside" width={80} />
              </a>
            </div>

            <div className={css.footer}>
              <h2 className={css.h2}>Don&apos;t have an account?</h2>

              <p className={css.caption}>
                Create a free account to save your favorite surf breaks and get instant
                Surf-Alerts on your personalised MySurf dashboard.
              </p>
              <a
                href="/sign_up"
                className={css.signup}
                onClick={(e) => {
                  e.preventDefault()
                  onSignUp()
                }}
              >
                Sign Up
              </a>
            </div>
          </form>
        )}
      </Form>
    </div>
  )
}
